// --------------------------------------------------------------
// Created On: 2021-09-01
// Author: Zachary Thomas
//
// Last Modified: 2026-08-28
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import {
  API,
  SECONDS_TO_WAIT_TO_RETURN_DEVICE_DATA,
  SECONDS_BETWEEN_DEVICE_READ_QUERIES,
  MAX_QUERIES_FOR_DEVICE_READ,
  MS_PER_SECOND,
} from "../../constants/miscellaneous";
import Spinner from "../Spinner/Spinner";
import useReduxSelector from "../../hooks/useReduxSelector";
import apiRequest from "../../utilities/api/apiRequest";
import styles from "./SampleDeviceDataButton.module.scss";

// When clicked, this button requests the current monitoring device to sample new data.
export default function SampleDeviceDataButton(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [currentIsoTime, setCurrentIsoTime] = useState<string | null>(null);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Poll sample data until we get new data or we time out.
  useEffect(() => {
    if (queriesRemaining > 0 && selectedAssetId !== null && currentIsoTime !== null) {
      const timerId = setTimeout(
        () => checkAssetUpdated(selectedAssetId, currentIsoTime, queriesRemaining),
        MS_PER_SECOND * SECONDS_BETWEEN_DEVICE_READ_QUERIES
      );

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [queriesRemaining, selectedAssetId, currentIsoTime]);

  // Samples the device of the provided asset to get the most current device data.
  function sampleDevice(assetId: number | null): void {
    if (assetId !== null) {
      setLoading(true);
      void apiRequest(`${API}/company/${currentUser.companyId}/asset/${assetId}/operation/sample-device`, "GET", null);

      const currentDate = new Date();
      if (props.onDeviceError !== undefined) {
        props.onDeviceError("");
      }
      setCurrentIsoTime(currentDate.toISOString().split(".")[0]);
      setQueriesRemaining(MAX_QUERIES_FOR_DEVICE_READ);
      setSelectedAssetId(assetId);
      setLoading(true);
      if (props.onIsSamplingChange !== undefined) {
        props.onIsSamplingChange(true);
      }
    }
  }

  // Check if a asset's data has been updated and send the appropriate response.
  async function checkAssetUpdated(assetId: number, isoTime: string, queriesRemaining: number): Promise<void> {
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/hasupdated/${isoTime}`,
      "GET",
      null
    )) as [Response, ResponseBody];

    if (response.ok) {
      if (responseBody.hasUpdated) {
        console.log(
          `New asset sample data found. Waiting ${SECONDS_TO_WAIT_TO_RETURN_DEVICE_DATA} seconds to batch results.`
        );
        setTimeout(getSampledData, SECONDS_TO_WAIT_TO_RETURN_DEVICE_DATA * MS_PER_SECOND);
      } else if (queriesRemaining <= 1) {
        // The asset failed to update.
        setLoading(false);
        if (props.onIsSamplingChange !== undefined) {
          props.onIsSamplingChange(false);
        }
        setCurrentIsoTime(null);
        setSelectedAssetId(null);
        setQueriesRemaining(0);
        if (props.onDeviceError !== undefined) {
          props.onDeviceError("Monitoring device was unable to take sample.");
        }
      } else {
        // The device is still in the process of updating.
        setQueriesRemaining(queriesRemaining - 1);
      }
    } else {
      // If we get an error, stop trying to process a device update.
      setLoading(false);
      if (props.onIsSamplingChange !== undefined) {
        props.onIsSamplingChange(false);
      }
      setCurrentIsoTime(null);
      setSelectedAssetId(null);
      setQueriesRemaining(0);
      if (props.onDeviceError !== undefined) {
        props.onDeviceError("Monitoring device was unable to take sample.");
      }
    }
  }

  // An updated sample exists and should be returned.
  function getSampledData(): void {
    setLoading(false);
    if (props.onIsSamplingChange !== undefined) {
      props.onIsSamplingChange(false);
    }
    setCurrentIsoTime(null);
    setSelectedAssetId(null);
    setQueriesRemaining(0);
    if (props.onDeviceRead !== undefined) {
      props.onDeviceRead();
    }
  }

  return (
    <Fragment>
      <Spinner loading={loading} />
      <button
        data-test="sample-device-button"
        className={`${props.smallButton ? styles.smallBody : styles.body} btn btn-primary ${props.className}`}
        type="button"
        disabled={props.disabled || selectedAssetId !== null}
        onClick={() => sampleDevice(props.assetId)}
      >
        <span
          className="d-none d-lg-inline-block d-xl-none d-xxl-inline-block pb-1 align-items-center"
          style={{ fontSize: "0.95rem" }}
        >
          {props.buttonName}
        </span>
        <span className="d-inline-block d-lg-none d-xl-inline-block d-xxl-none" style={{ fontSize: "0.95rem" }}>
          {props.buttonName}
        </span>
      </button>
    </Fragment>
  );
}

interface Props {
  assetId: number | null;
  smallButton?: boolean;
  disabled?: boolean;
  className?: string;
  buttonName: string;
  onIsSamplingChange?: (isSampling: boolean) => void;
  onDeviceRead?: () => void;
  onDeviceError?: (errorMessage: string) => void;
}

interface ResponseBody {
  hasUpdated: boolean;
}
